import type { PolicyViewModel } from 'src/api/types/PolicyViewModel';
import type { StateLookupOption, CodeDescriptionLookupOption } from 'src/api/services/policy.service';

import Grid from '@mui/material/Grid';
import { Box, MenuItem, TextField, Typography } from '@mui/material';

import { renderEmployeeLeasingTypeWithTooltip } from 'src/sections/employer-data/shared/utils/policyUtils';

import { LEGAL_NATURE_OTHER_CODE } from './policy-information.helpers';
import { CoverageReadOnlyFields } from './policy-information-read-only-fields';
import {
  toSelectOptions,
  type MarketOption,
  formFieldWrapperSx,
  type PolicyDraftSetter,
  buildSelectOptionsWithLegacyValue,
} from './policy-information-edit.helpers';

import type { PolicyEditDraft, PolicyFieldErrors } from './policy-information.types';

type PolicyInformationCoverageFieldsProps = {
  comboId: number;
  viewPolicy: PolicyViewModel;
  draft: PolicyEditDraft;
  setDraft: PolicyDraftSetter;
  stateLookups: StateLookupOption[];
  legalNatureLookups: CodeDescriptionLookupOption[];
  retroRatingLookups: CodeDescriptionLookupOption[];
  marketOptions: MarketOption[];
  fieldErrors: PolicyFieldErrors;
};

export function PolicyInformationCoverageFields({
  comboId,
  viewPolicy,
  draft,
  setDraft,
  stateLookups,
  legalNatureLookups,
  retroRatingLookups,
  marketOptions,
  fieldErrors,
}: Readonly<PolicyInformationCoverageFieldsProps>) {
  const legalNatureOptions = buildSelectOptionsWithLegacyValue(
    draft.legalNatureCode,
    toSelectOptions(legalNatureLookups)
  );
  const retroRatingOptions = buildSelectOptionsWithLegacyValue(
    draft.retroRatingCode,
    toSelectOptions(retroRatingLookups),
    viewPolicy.retroRatingTypeEnumDescription
  );
  const minimumPremiumStateOptions = buildSelectOptionsWithLegacyValue(
    draft.minimumPremiumStateCode > 0 ? String(draft.minimumPremiumStateCode) : '',
    stateLookups.map((lookup) => ({
      value: String(lookup.stateCode),
      label: `${lookup.stateAbbreviation} - ${lookup.stateName}`,
    }))
  );
  const marketSelectOptions = buildSelectOptionsWithLegacyValue(
    draft.planTypeCode,
    marketOptions.map((lookup) => ({ value: lookup.code, label: lookup.description }))
  );

  return (
    <Box sx={{ padding: '1.5rem' }}>
      <Typography variant="h5" sx={{ color: 'text.primary' }} data-testid="coverage-profile-title">
        Coverage
      </Typography>
      <Grid container spacing={2} columns={16}>
        <CoverageReadOnlyFields comboId={comboId} viewPolicy={viewPolicy} />
        <Grid size={{ xs: 8, md: 4 }}>
          <Box sx={formFieldWrapperSx}>
            <TextField
              fullWidth
              size="small"
              label="Interstate risk ID"
              value={draft.interstateRiskNumber}
              onChange={(event) =>
                setDraft((current) => ({ ...current, interstateRiskNumber: event.target.value }))
              }
              slotProps={{ htmlInput: { maxLength: 9 } }}
              error={Boolean(fieldErrors.interstateRiskNumber)}
              helperText={fieldErrors.interstateRiskNumber}
              data-testid="interstate-risk-id-input"
            />
          </Box>
        </Grid>
        <Grid size={{ xs: 8, md: 4 }}>
          <Box sx={formFieldWrapperSx} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Legal nature"
              value={draft.legalNatureCode}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  legalNatureCode: event.target.value,
                  legalNatureOther:
                    event.target.value === LEGAL_NATURE_OTHER_CODE ? current.legalNatureOther : '',
                }))
              }
              error={Boolean(fieldErrors.legalNatureCode)}
              helperText={fieldErrors.legalNatureCode}
              data-testid="legal-nature-select"
            >
              {legalNatureOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {draft.legalNatureCode === LEGAL_NATURE_OTHER_CODE && (
              <TextField
                fullWidth
                size="small"
                label="Legal nature (other)"
                value={draft.legalNatureOther}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, legalNatureOther: event.target.value }))
                }
                slotProps={{ htmlInput: { maxLength: 20 } }}
                error={Boolean(fieldErrors.legalNatureOther)}
                helperText={fieldErrors.legalNatureOther}
                sx={{ mt: 2 }}
                data-testid="legal-nature-other-input"
              />
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 8, md: 4 }}>
          {renderEmployeeLeasingTypeWithTooltip(viewPolicy.policyEmployeeLeasingPolicyType)}
        </Grid>
        <Grid size={{ xs: 8, md: 4 }}>
          <Box sx={formFieldWrapperSx}>
            <TextField
              select
              fullWidth
              size="small"
              label="Retro rating"
              value={draft.retroRatingCode}
              onChange={(event) =>
                setDraft((current) => ({ ...current, retroRatingCode: event.target.value }))
              }
              error={Boolean(fieldErrors.retroRatingCode)}
              helperText={fieldErrors.retroRatingCode}
              data-testid="retro-rating-select"
            >
              {retroRatingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Grid>
        <Grid size={{ xs: 8, md: 4 }}>
          <Box sx={formFieldWrapperSx}>
            <TextField
              select
              fullWidth
              size="small"
              label="Minimum premium state"
              value={draft.minimumPremiumStateCode > 0 ? String(draft.minimumPremiumStateCode) : ''}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  minimumPremiumStateCode: Number(event.target.value),
                }))
              }
              error={Boolean(fieldErrors.minimumPremiumStateCode)}
              helperText={fieldErrors.minimumPremiumStateCode}
              data-testid="minimum-premium-state-select"
            >
              {minimumPremiumStateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Grid>
        <Grid size={{ xs: 8, md: 4 }}>
          <Box sx={formFieldWrapperSx}>
            <TextField
              select
              fullWidth
              size="small"
              label="Market"
              value={draft.planTypeCode}
              onChange={(event) =>
                setDraft((current) => ({ ...current, planTypeCode: event.target.value }))
              }
              error={Boolean(fieldErrors.planTypeCode)}
              helperText={fieldErrors.planTypeCode}
              data-testid="market-select"
            >
              {marketSelectOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
}
